/**
 * handshakeService.ts — HTTP client for /handshakes endpoints.
 *
 * Wraps the backend Handshake routes built in Block 4. The 4-state machine:
 *   requested → acknowledged → confirmed → closed
 *   (+ rejected, cancelled branches)
 */
import api from "./api";

// ─── Types (mirror backend Handshake schema) ───────────────────────

export type HandshakeStatus =
  | "requested"
  | "acknowledged"
  | "confirmed"
  | "closed"
  | "rejected"
  | "cancelled";

export interface HandshakeLine {
  productId?: string | null;
  productName: string;
  vendorSku?: string | null;
  quantity: number;
  unitPriceMinor: number;
  lineTotalMinor: number;
  note?: string | null;
}

export interface ConversationEntry {
  _id?: string;
  kind:
    | "state_transition"
    | "message"
    | "delegation"
    | "receipt_generated"
    | "system";
  title: string;
  body?: string | null;
  actorUserId?: string | null;
  actorName?: string | null;
  actorRole?: string | null;
  actorSide?: "distributor" | "vendor" | "system";
  fromState?: string | null;
  toState?: string | null;
  metadata?: Record<string, unknown>;
  at: string;
}

export interface CustomerReceipt {
  format: "pdf" | "sms" | "whatsapp" | "email";
  url?: string | null;
  sentTo?: string | null;
  sentAt: string;
  generatedByUserId?: string | null;
}

export interface Handshake {
  _id: string;
  connectionId: string;
  distributorCompanyId: string;
  distributorBusinessId: string;
  vendorCompanyId?: string | null;
  vendorBusinessId?: string | null;
  vendorOnEaze: boolean;
  autoPostOnClose: boolean;
  handshakeId: string;
  status: HandshakeStatus;
  initiatedBy: "distributor" | "vendor";
  lines: HandshakeLine[];
  grandTotalMinor: number;
  currency: string;
  expectedDeliveryAt?: string | null;
  announcementId?: string | null;
  delegatedToCashierId?: string | null;
  delegatedAt?: string | null;
  delegatedByUserId?: string | null;
  requestedByUserId?: string | null;
  acknowledgedByUserId?: string | null;
  acknowledgedAt?: string | null;
  confirmedByUserId?: string | null;
  confirmedAt?: string | null;
  closedByUserId?: string | null;
  closedAt?: string | null;
  rejectedByUserId?: string | null;
  rejectionReason?: string | null;
  cancelledByUserId?: string | null;
  cancellationReason?: string | null;
  vendorSaleJournalEntryId?: string | null;
  distributorReceiptJournalEntryId?: string | null;
  inventoryReceiptId?: string | null;
  vendorOrderId?: string | null;
  customerReceipts: CustomerReceipt[];
  conversation: ConversationEntry[];
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
  myRole: "distributor" | "vendor";
}

export interface HandshakeListResponse {
  handshakes: Handshake[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface HandshakeListFilters {
  status?: HandshakeStatus;
  connectionId?: string;
  delegatedToCashierId?: string;
  page?: number;
  limit?: number;
}

export interface CreateHandshakeInput {
  connectionId: string;
  lines: Array<{
    productId?: string | null;
    productName: string;
    vendorSku?: string | null;
    quantity: number;
    unitPriceMinor: number;
    note?: string | null;
  }>;
  currency?: string;
  expectedDeliveryAt?: string;
  notes?: string;
}

export interface AcknowledgeInput {
  counterLines?: CreateHandshakeInput["lines"];
  expectedDeliveryAt?: string;
  notes?: string;
}

export interface CustomerReceiptInput {
  format: "pdf" | "sms" | "whatsapp" | "email";
  url?: string;
  sentTo?: string;
}

// ─── API surface ───────────────────────────────────────────────────

export const handshakeService = {
  list: async (
    filters: HandshakeListFilters = {},
  ): Promise<HandshakeListResponse> => {
    const params: Record<string, string | number> = {};
    if (filters.status) params.status = filters.status;
    if (filters.connectionId) params.connectionId = filters.connectionId;
    if (filters.delegatedToCashierId)
      params.delegatedToCashierId = filters.delegatedToCashierId;
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
    const res = await api.get<HandshakeListResponse>("/handshakes", { params });
    return res.data;
  },

  get: async (id: string): Promise<Handshake> => {
    const res = await api.get<{ handshake: Handshake }>(`/handshakes/${id}`);
    return res.data.handshake;
  },

  create: async (input: CreateHandshakeInput): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>("/handshakes", input);
    return res.data.handshake;
  },

  acknowledge: async (
    id: string,
    input: AcknowledgeInput = {},
  ): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>(
      `/handshakes/${id}/acknowledge`,
      input,
    );
    return res.data.handshake;
  },

  confirm: async (id: string, notes?: string): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>(
      `/handshakes/${id}/confirm`,
      { notes },
    );
    return res.data.handshake;
  },

  close: async (id: string, notes?: string): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>(
      `/handshakes/${id}/close`,
      { notes },
    );
    return res.data.handshake;
  },

  reject: async (id: string, reason?: string): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>(
      `/handshakes/${id}/reject`,
      { reason },
    );
    return res.data.handshake;
  },

  cancel: async (id: string, reason?: string): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>(
      `/handshakes/${id}/cancel`,
      { reason },
    );
    return res.data.handshake;
  },

  delegate: async (id: string, cashierUserId: string): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>(
      `/handshakes/${id}/delegate`,
      { cashierUserId },
    );
    return res.data.handshake;
  },

  postMessage: async (id: string, body: string): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>(
      `/handshakes/${id}/message`,
      { body },
    );
    return res.data.handshake;
  },

  recordCustomerReceipt: async (
    id: string,
    input: CustomerReceiptInput,
  ): Promise<Handshake> => {
    const res = await api.post<{ handshake: Handshake }>(
      `/handshakes/${id}/customer-receipt`,
      input,
    );
    return res.data.handshake;
  },
};

export default handshakeService;
